import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Field } from "@/components/forms/fields";
import PhoneInput from "@/components/forms/PhoneInput";

const empty = {
  nombre_completo: "",
  telefono: "", telefono_pais: "MX", telefono_e164: null,
  correo: "",
  pais: "MX", region: "", ciudad: "",
  puede_acudir: "no_definido",
  tiempo_estimado: "",
  disponibilidad: "",
  idioma: "es",
  notas: "",
};

const PUEDE_ACUDIR = [
  ["si", "Sí"], ["no", "No"], ["horario", "Sólo en determinado horario"], ["no_definido", "No definido"],
];
const TIEMPOS = [["<15", "Menos de 15 min"], ["15-45", "15–45 min"], [">45", "Más de 45 min"]];
const DISPONIBILIDAD = [
  ["siempre", "Siempre"], ["diurno", "Diurno"], ["nocturno", "Nocturno"],
  ["laboral", "Horario laboral"], ["personalizado", "Personalizado"],
];
const IDIOMAS = [["es", "Español"], ["en", "Inglés"], ["ambos", "Ambos"]];

export default function ContactForm({ open, onOpenChange, contacto, onSaved, personaId = null, relacion = null }) {
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const cargadoRef = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!open) { cargadoRef.current = null; return; }
    const clave = contacto?.id || "nuevo";
    if (cargadoRef.current === clave) return;
    cargadoRef.current = clave;
    setF(contacto ? { ...empty, ...contacto } : empty);
  }, [open, contacto]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f };
      let res;
      if (contacto?.id) res = await api.put(`/contactos/${contacto.id}`, payload);
      // v2.13.4 — dentro del expediente de una persona el contacto se crea Y se vincula en el
      // MISMO paso: no hay que buscarlo después en ningún catálogo.
      else if (personaId) res = await api.post(`/personas/${personaId}/contactos`, { contacto: payload, ...(relacion || {}) });
      else res = await api.post("/contactos", payload);
      toast.success("Contacto guardado.");
      onSaved && onSaved(personaId && !contacto?.id ? res.data.contacto : res.data,
                         personaId && !contacto?.id ? res.data.contactos : null);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const puedeAcude = f.puede_acudir === "si" || f.puede_acudir === "horario";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#012472]">
            {contacto?.id ? "Editar contacto" : "Nuevo contacto"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Nombre completo">
            <Input data-testid="contacto-nombre-input" className="rounded-xl" value={f.nombre_completo} onChange={(e) => set("nombre_completo", e.target.value)} />
          </Field>
          <Field label="Teléfono (internacional)">
            <PhoneInput testId="contacto-telefono" value={f} onChange={(t) => setF((p) => ({ ...p, ...t }))} />
          </Field>
          <Field label="Correo (opcional)">
            <Input className="rounded-xl" value={f.correo} onChange={(e) => set("correo", e.target.value)} />
          </Field>

          <div className="border-t border-slate-100 pt-3">
            <p className="mb-2 text-sm font-medium text-[#012472]">Ubicación del contacto</p>
            <div className="grid grid-cols-3 gap-3">
              <Field label="País">
                <Select value={f.pais || ""} onValueChange={(v) => set("pais", v)}>
                  <SelectTrigger data-testid="contacto-pais" className="rounded-xl"><SelectValue placeholder="País" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MX">México</SelectItem>
                    <SelectItem value="US">Estados Unidos</SelectItem>
                    <SelectItem value="CA">Canadá</SelectItem>
                    <SelectItem value="ES">España</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Estado / región">
                <Input className="rounded-xl" value={f.region} onChange={(e) => set("region", e.target.value)} />
              </Field>
              <Field label="Ciudad (opcional)">
                <Input className="rounded-xl" value={f.ciudad} onChange={(e) => set("ciudad", e.target.value)} />
              </Field>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="¿Puede acudir físicamente?">
                <Select value={f.puede_acudir || "no_definido"} onValueChange={(v) => set("puede_acudir", v)}>
                  <SelectTrigger data-testid="contacto-puede-acudir" className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{PUEDE_ACUDIR.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              {puedeAcude && (
                <Field label="Tiempo estimado de llegada">
                  <Select value={f.tiempo_estimado || ""} onValueChange={(v) => set("tiempo_estimado", v)}>
                    <SelectTrigger data-testid="contacto-tiempo" className="rounded-xl"><SelectValue placeholder="Tiempo" /></SelectTrigger>
                    <SelectContent>{TIEMPOS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              )}
              <Field label="Disponibilidad">
                <Select value={f.disponibilidad || ""} onValueChange={(v) => set("disponibilidad", v)}>
                  <SelectTrigger data-testid="contacto-disponibilidad" className="rounded-xl"><SelectValue placeholder="Disponibilidad" /></SelectTrigger>
                  <SelectContent>{DISPONIBILIDAD.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Idioma">
                <Select value={f.idioma || "es"} onValueChange={(v) => set("idioma", v)}>
                  <SelectTrigger data-testid="contacto-idioma" className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{IDIOMAS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
          </div>

          <Field label="Notas">
            <Textarea className="rounded-xl" value={f.notas} onChange={(e) => set("notas", e.target.value)} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="contacto-guardar-button" disabled={saving} className="rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white" onClick={save}>
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
